import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BellRing, Save, Send, Loader2, Smartphone, Mail, Wrench } from "lucide-react";
import { toast } from "sonner";

export function NotificationSettings() {
  const [n, setN] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { api.get("/settings/notifications").then((r) => setN(r.data)); }, []);
  const set = (k, v) => setN((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { forward_email_enabled, forward_email, sms_enabled, sms_number, sms_carrier, include_body, maintenance_alerts } = n;
      await api.put("/settings/notifications", { forward_email_enabled, forward_email, sms_enabled, sms_number, sms_carrier, include_body, maintenance_alerts });
      toast.success("Inbox forwarding saved");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true); setResult(null);
    try {
      const { data } = await api.post("/settings/notifications/test");
      setResult(data);
      const ok = Object.values(data).every((r) => r.ok);
      (ok ? toast.success : toast.warning)(ok ? "Test sent — check your inbox / phone" : "Test sent with some failures — see details");
    } catch (e) { toast.error(apiErr(e)); } finally { setTesting(false); }
  };

  if (!n) return null;
  const carrier = n.carriers.find((c) => c.key === n.sms_carrier);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="notification-settings">
      <div className="flex items-start gap-3 mb-1">
        <span className="h-9 w-9 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center shrink-0"><BellRing className="h-4 w-4" /></span>
        <div>
          <h2 className="font-display font-bold text-lg text-slate-900">Inbox forwarding</h2>
          <p className="text-sm text-slate-500">Get notified outside the app when a tenant messages you or submits a maintenance request. Save your settings first, then send a test.</p>
        </div>
      </div>

      <div className="mt-5 space-y-4">
        <div className="rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-800"><Mail className="h-4 w-4 text-slate-400" /> Forward to email</div>
            <Switch checked={n.forward_email_enabled} onCheckedChange={(v) => set("forward_email_enabled", v)} data-testid="notify-email-toggle" />
          </div>
          <div className={`mt-3 space-y-1.5 ${n.forward_email_enabled ? "" : "opacity-50"}`}>
            <Label className="text-xs">Send to (leave blank to use your login email: {n.login_email})</Label>
            <Input type="email" value={n.forward_email} onChange={(e) => set("forward_email", e.target.value)} placeholder={n.login_email} data-testid="notify-email" disabled={!n.forward_email_enabled} />
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-800"><Smartphone className="h-4 w-4 text-slate-400" /> Forward as SMS text</div>
            <Switch checked={n.sms_enabled} onCheckedChange={(v) => set("sms_enabled", v)} data-testid="notify-sms-toggle" />
          </div>
          <div className={`mt-3 grid sm:grid-cols-2 gap-3 ${n.sms_enabled ? "" : "opacity-50"}`}>
            <div className="space-y-1.5"><Label className="text-xs">US mobile number</Label>
              <Input value={n.sms_number} onChange={(e) => set("sms_number", e.target.value)} placeholder="(555) 123-4567" data-testid="notify-sms-number" disabled={!n.sms_enabled} /></div>
            <div className="space-y-1.5"><Label className="text-xs">Carrier</Label>
              <Select value={n.sms_carrier} onValueChange={(v) => set("sms_carrier", v)} disabled={!n.sms_enabled}>
                <SelectTrigger data-testid="notify-sms-carrier"><SelectValue /></SelectTrigger>
                <SelectContent>{n.carriers.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div className={`mt-3 flex items-center justify-between gap-3 ${n.sms_enabled ? "" : "opacity-50"}`}>
            <Label className="text-xs">Include the first line of the message in the text</Label>
            <Switch checked={n.include_body} onCheckedChange={(v) => set("include_body", v)} disabled={!n.sms_enabled} data-testid="notify-sms-body" />
          </div>
          <p className="text-[11px] text-slate-400 mt-3 leading-relaxed">
            Texts are delivered through your carrier's free email-to-SMS gateway{carrier ? ` (${carrier.gateway})` : ""}. No Twilio account needed, but delivery can be delayed or filtered by some carriers.
          </p>
        </div>

        <div className="rounded-xl border border-slate-200 p-4 flex items-center justify-between gap-3">
          <div className="flex items-start gap-2">
            <Wrench className="h-4 w-4 text-slate-400 mt-0.5" />
            <div>
              <div className="text-sm font-semibold text-slate-800">Also forward new maintenance requests</div>
              <p className="text-xs text-slate-500">Get the title, priority, property and first line of the request by email/SMS the moment a tenant submits it.</p>
            </div>
          </div>
          <Switch checked={n.maintenance_alerts} onCheckedChange={(v) => set("maintenance_alerts", v)} data-testid="notify-maintenance-toggle" />
        </div>

        {result && (
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-xs space-y-1" data-testid="notify-test-result">
            {Object.entries(result).map(([k, r]) => (
              <div key={k} className={r.ok ? "text-emerald-700" : "text-rose-700"}>{k.toUpperCase()}: {r.ok ? `sent to ${r.to}` : `failed — ${r.error}`}</div>
            ))}
          </div>
        )}

        <div className="flex gap-2">
          <Button variant="outline" className="gap-2" onClick={test} disabled={testing} data-testid="notify-test">
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send test
          </Button>
          <Button className="gap-2 flex-1" onClick={save} disabled={saving} data-testid="notify-save"><Save className="h-4 w-4" /> {saving ? "Saving…" : "Save forwarding"}</Button>
        </div>
      </div>
    </div>
  );
}
